import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Flex,
  Button,
  Text,
  Input,
  IconButton,
  FormControl,
  FormErrorMessage
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

const UploadApplications_Table = forwardRef(({ title, textColor, bgButton }, ref) => {
  const [rows, setRows] = useState([
    { opsaa: '', name: '', final: '', amount: '', errors: {} },
  ]);

  const validateField = (field, value) => {
    switch (field) {
      case 'amount':
        return value === '' || !isNaN(value);
      case 'final':
        return value === '' || value === '0' || value === '1';
      case 'opsaa':
        return /^00\d{8}$/.test(value); // starts with 00 and has 10 digits
      default:
        return true;
    }
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    const isValid = validateField(field, value);
    updatedRows[index][field] = value;
    updatedRows[index].errors = {
      ...updatedRows[index].errors,
      [field]: !isValid
    };
    setRows(updatedRows);
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      { opsaa: '', name: '', final: '', amount: '', errors: {} },
    ]);
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  useImperativeHandle(ref, () => ({
    getApplicationsData: () => {
      const hasErrors = rows.some(row =>
        Object.values(row.errors || {}).some(error => error === true)
      );
      if (hasErrors) {
        alert("Υπάρχουν λάθη στις γραμμές. Διορθώστε τα πριν την εξαγωγή.");
        return null;
      }

      const sanitized = rows.map(row => Object.values({
        opsaa: row.opsaa,
        name: row.name,
        final: row.final,
        amount: row.amount
      }));

      return JSON.stringify([
        ["opsaa", "name", "final", "amount"],
        ...sanitized
      ]).replace(/&/g, 'και');
    }
  }));

  return (
    <Card p="16px" mb="4px" w="100%">
      <CardHeader>
        <Flex justify="space-between" align="center" minHeight="60px" w="100%">
          <Text fontSize="lg" color="white" fontWeight="bold">
            {title}
          </Text>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box overflowX="auto">
          <Flex direction="column" gap={3}>
            {/* Header Row */}
            <Flex gap={2} fontWeight="bold" color={textColor}>
              <Text w="160px">Κωδικός ΟΠΣΑΑ</Text>
              <Text w="160px">Επωνυμία</Text>
              <Text w="160px">Τελική Πληρωμή (0=ΟΧΙ/1=ΝΑΙ)</Text>
              <Text w="160px">Αιτούμενο ποσό</Text>
              <Text w="40px" />
            </Flex>

            {/* Data Rows */}
            {rows.map((row, index) => (
              <Flex key={index} gap={2} align="center">
                <FormControl isInvalid={row.errors?.opsaa} w="160px">
                  <Input
                    placeholder="ΟΠΣΑΑ"
                    value={row.opsaa}
                    onChange={(e) => handleChange(index, 'opsaa', e.target.value)}
                  />
                  {row.errors?.opsaa && (
                    <FormErrorMessage>Πρέπει να ξεκινά με 00 και να έχει 10 ψηφία</FormErrorMessage>
                  )}
                </FormControl>

                <Input
                  w="160px"
                  placeholder="Επωνυμία"
                  value={row.name}
                  onChange={(e) => handleChange(index, 'name', e.target.value)}
                />

                <FormControl isInvalid={row.errors?.final} w="160px">
                  <Input
                    placeholder="0 ή 1"
                    value={row.final}
                    onChange={(e) => handleChange(index, 'final', e.target.value)}
                  />
                  {row.errors?.final && (
                    <FormErrorMessage>Μόνο 0 ή 1</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl isInvalid={row.errors?.amount} w="160px">
                  <Input
                    placeholder="Ποσό"
                    value={row.amount}
                    onChange={(e) => handleChange(index, 'amount', e.target.value)}
                  />
                  {row.errors?.amount && (
                    <FormErrorMessage>Μόνο αριθμός</FormErrorMessage>
                  )}
                </FormControl>

                <IconButton
                  icon={<DeleteIcon />}
                  onClick={() => handleRemoveRow(index)}
                  colorScheme="red"
                  variant="outline"
                  aria-label="Διαγραφή"
                  w="40px"
                />
              </Flex>
            ))}

            <Button
              leftIcon={<AddIcon />}
              onClick={handleAddRow}
              bg={bgButton}
              color="white"
              mt={2}
              w="fit-content"
              alignSelf="flex-start"
            >
              Προσθήκη γραμμής
            </Button>
          </Flex>
        </Box>
      </CardBody>
    </Card>
  );
});

export default UploadApplications_Table;
